import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Boxes } from "lucide-react";

const NAV = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services", testid: "nav-services" },
  { label: "Why Us", href: "#differentials" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <motion.header
      initial={{ y: -32, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-2 group"
        >
          <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#FF6B00] to-[#00FF66]">
            <Boxes className="h-4 w-4 text-black" strokeWidth={2.5} />
          </span>
          <span className="font-[Unbounded] text-base sm:text-lg font-bold tracking-tight">
            Anima<span className="text-[#FF6B00]">Software</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={n.testid || `nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm text-gray-300 hover:text-white transition-colors duration-200 relative group"
            >
              {n.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-gradient-to-r from-[#FF6B00] to-[#00FF66] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <a
          href="#contact"
          data-testid="nav-cta-quote"
          className="hidden md:inline-flex items-center gap-2 px-5 h-10 rounded-full bg-gradient-to-r from-[#FF6B00] to-[#00FF66] text-black font-semibold text-sm hover:opacity-90 hover:-translate-y-0.5 transition-all duration-300"
        >
          Get a Quote
        </a>

        <button
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/10 bg-white/5"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-black/85 backdrop-blur-xl"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={close}
                  className="text-gray-200 text-base"
                  data-testid={`mobile-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={close}
                data-testid="mobile-cta-quote"
                className="mt-2 inline-flex items-center justify-center px-5 h-11 rounded-full bg-gradient-to-r from-[#FF6B00] to-[#00FF66] text-black font-semibold text-sm"
              >
                Get a Quote
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
